<template>
    <div class="w-full p-4 lg:px-10 lg:py-5">
        <div class="flex flex-col justify-between items-start mb-5">
            <h2 class="font-mulish text-2xl text-[#053A51] font-bold mb-5">
                Upload Bulk Staff Promotion
            </h2>
            <p class="font-mulish text-sm font-semibold text-[#053A51]">
                Use this tool to initiate bulk service promotion for existing staff members
            </p>

            <div class="w-full mt-10 bg-[#ffff] p-0 lg:p-6">
                <form @submit.prevent="handleSubmit" class="space-y-5">
                    <div class="bg-[#F9F9F9] rounded-lg flex flex-col gap-4 shadow p-6 w-full lg:w-[698px]">
                        <!-- Batch Name -->
                        <div>
                            <label class="block text-base font-medium text-gray-700 mb-1">
                                Batch Name <span class="text-red-500">*</span>
                            </label>
                            <input v-model="form.batchName" type="text" required
                                class="w-full border rounded-lg px-3 py-2 h-[56px] border-[#C7C7C7] focus:outline-none focus:ring focus:ring-blue-400"
                                placeholder="2024 - 2025 Staff promotion" />
                        </div>

                        <!-- Excel File Upload -->
                        <div class="mb-0">
                            <label class="block text-base font-medium text-gray-700 mb-1">
                                Choose Excel File <span class="text-red-500">*</span>
                            </label>
                            <div
                                class="relative w-full border border-[#C7C7C7] rounded-md h-[56px] flex items-center overflow-hidden">
                                <span class="px-3 text-gray-500 truncate">
                                    {{ fileName || '-Choose excel file to upload -' }}
                                </span>
                                <button type="button"
                                    class="absolute right-2 h-[44px] bg-[#80B7FF] text-white px-4 rounded-lg hover:bg-[#3c81da] transition"
                                    @click="$refs.excelInput.click()">
                                    Browse File
                                </button>
                                <input type="file" ref="excelInput" accept=".xlsx,.xls"
                                    class="absolute inset-0 opacity-0 cursor-pointer" @change="handleExcelUpload" />
                            </div>
                            <a :href="approvedTemplateUrl" download
                                class="text-[#0090FF] underline m-0 p-0 italic text-sm hover:text-[#3771af]">
                                Click to download bulk promotion template
                            </a>
                        </div>

                        <!-- Approved PDF Upload -->
                        <div>
                            <label class="block text-base font-medium text-gray-700 mb-1">
                                Approved List of Promotions <span class="text-red-500">*</span>
                            </label>
                            <div
                                class="relative w-full border border-[#C7C7C7] rounded-lg h-[56px] flex items-center cursor-pointer overflow-hidden">
                                <span class="px-3 text-gray-500 truncate">
                                    {{ approvedFileName || '-Choose approved PDF -' }}
                                </span>
                                <button type="button"
                                    class="absolute right-2 h-[44px] bg-[#80B7FF] text-white px-4 rounded-lg hover:bg-[#3c81da] transition">
                                    Browse File
                                </button>
                                <input type="file" ref="approvedInput" accept=".pdf"
                                    class="absolute inset-0 opacity-0 cursor-pointer" @change="handleApprovedUpload" />
                            </div>
                            <p class="text-[#053A51] font-mulish italic text-sm">
                                Scan and upload the approved promotion list
                                <span class="text-[#FF0000]">(PDF, max 5MB)</span>
                            </p>
                        </div>
                    </div>

                    <!-- Buttons -->
                    <div class="w-full lg:w-[698px] flex justify-end items-end gap-3 pt-4">
                        <button type="button" @click="handleCancel"
                            class="lg:w-[201px] w-auto h-[56px] px-4 py-2 rounded-lg border bg-[#FFECEC] text-[#484848] hover:bg-[#b9b5b5]">
                            Cancel
                        </button>
                        <button type="submit" :disabled="submitting"
                            class="lg:w-[201px] w-auto h-[56px] px-4 py-2 rounded-lg bg-[#1A8103] text-white hover:bg-[#265c19] disabled:opacity-50">
                            {{ submitting ? 'Submitting...' : 'Upload' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import router from "@/router";
import Swal from "sweetalert2"

export default {
    name: "Upload Bulk Promotion",
    props: ["onSubmit"],
    data() {
        return {
            batchId: "",
            form: {
                batchName: "",
                excelFile: null,
                approvedFile: null,
            },
            fileName: "",
            approvedFileName: "",
            submitting: false,
            approvedTemplateUrl:
                "https://abiaoneid.s3.eu-west-2.amazonaws.com/email_template/Promotion-Template.xlsx",
        };
    },
    methods: {
        handleExcelUpload(e) {
            const file = e.target.files[0];
            if (file && file.size > 5 * 1024 * 1024) {
                this.$swal({
                    icon: "error",
                    text: "Excel file must not exceed 5MB",
                });
                e.target.value = null;
                return;
            }
            this.form.excelFile = file;
            this.fileName = file.name;
        },
        handleApprovedUpload(e) {
            const file = e.target.files[0];
            if (file && file.size > 5 * 1024 * 1024) {
                alert("PDF file must not exceed 5MB");
                e.target.value = null;
                return;
            }
            this.form.approvedFile = file;
            this.approvedFileName = file.name;
        },
        handleCancel() {
            this.form = {
                batchName: "",
                excelFile: null,
                approvedFile: null,
            };
            this.fileName = "";
            this.approvedFileName = "";
        },
        async handleSubmit() {
            if (!this.form.batchName || !this.form.excelFile || !this.form.approvedFile) {
                this.$swal({
                    icon: "error",
                    text: "Fill all required fields",
                });
                return;
            }

            // Show confirmation modal before uploading
            const confirm = await this.$swal({
                title: "Are you sure?",
                text: "You are about to upload this promotion batch. Please confirm before proceeding.",
                icon: "warning",
                width: 350,
                showCancelButton: true,
                confirmButtonColor: "#1A8103",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, Upload",
                cancelButtonText: "Cancel",
            });

            if (!confirm.isConfirmed) return;

            this.submitting = true;
            try {
                const formData = new FormData();
                formData.append("batchName", this.form.batchName);
                formData.append("staffPromotionFile", this.form.excelFile);
                formData.append("promotionEvidence", this.form.approvedFile);

                const response = await this.$http.post("/staff-promotion/bulk-upload", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });

                const data = response.data;
                Swal.fire({
                    title: "Successful",
                    text: "Promotion list uploaded successfully.",
                    icon: "success",
                    width: 350,
                    showCancelButton: false,
                    confirmButtonColor: "#1A8103",
                    confirmButtonText: "Ok",
                    customClass: {
                        popup: 'rounded-2xl p-4',
                        title: 'text-lg font-semibold text-gray-800 mb-1',
                        htmlContainer: 'text-sm text-gray-600',
                        confirmButton: 'px-20 py-2 rounded-lg bg-green-700 text-white hover:bg-green-800'
                    }
                }).then(() => {
                    this.onSubmit && this.onSubmit(data);
                    router.push(`/admin/promotion/bulk-upload-status/${data.data}`);
                    this.handleCancel();
                });
            } catch (error) {
                console.error(error);
                this.$swal({
                    icon: "error",
                    text: "Failed to upload promotion list.",
                });
            } finally {
                this.submitting = false;
            }
        }
    },
};
</script>
